import posthog from 'posthog-js';
import { useFeatureFlagEnabled as usePostHogFeatureFlagEnabled } from 'posthog-js/react';

import { DOMAIN_LOCALES } from '@shared/const';

export const environment = import.meta.env.MODE;
export const clientUrl = import.meta.env.VITE_CLIENT_URL;

export const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
export const posthogHost =
  import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';
export const isPostHogEnabled = Boolean(posthogKey);

export const getDomainName = () => {
  const { hostname } = window.location;

  const domain = hostname.split('.').pop();

  return domain in DOMAIN_LOCALES ? domain : 'no';
};

export const getGraphQLEndpoint = () => {
  const domain = getDomainName();
  switch (domain) {
    case 'es':
      return import.meta.env.VITE_GRAPHQL_ENDPOINT_ES;
    case 'no':
    default:
      return import.meta.env.VITE_GRAPHQL_ENDPOINT_NO;
  }
};

export const getLocale = () => {
  // Not allowing locale change in backoffice for now
  const domain = getDomainName();

  return DOMAIN_LOCALES[domain][0];
};

export const getStaticImagesHostname = () => {
  const domain = getDomainName();

  switch (domain) {
    case 'es':
      return import.meta.env.VITE_STATIC_IMAGES_HOSTNAME_ES;
    case 'no':
    default:
      return import.meta.env.VITE_STATIC_IMAGES_HOSTNAME_NO;
  }
};

// Feature flags are evaluated through PostHog. Falls closed to `false` when
// PostHog is not configured (no key) or a flag is unknown/not yet loaded.
export const isFeatureEnabled = (featureName) => {
  if (!isPostHogEnabled) {
    return false;
  }

  return posthog.isFeatureEnabled(featureName) ?? false;
};

// Reactive counterpart to isFeatureEnabled, for component-level use. Fails
// closed the same way; devOverride (e.g. a VITE_ dev-only env check) forces
// the flag on regardless of what PostHog reports.
//
// The PostHog hook is always called unconditionally (react-hooks/rules-of-hooks
// requires this) even when PostHog isn't configured; isPostHogEnabled gates
// its return value afterwards instead of short-circuiting the call itself.
export const useFeatureFlagEnabled = (featureName, devOverride) => {
  const flagEnabled = usePostHogFeatureFlagEnabled(featureName);
  return Boolean(devOverride) || (isPostHogEnabled && Boolean(flagEnabled));
};

export const COMPLETION_INTEGRITY_FEATURE_FLAG =
  'backoffice-course-completion-integrity';
